import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MagnifyingGlass, Plus, Users, UserPlus, Trash, Pencil, Receipt, Baby, X,
  BellRinging, ChatCircleText, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  addFamilyMember,
  createFamily,
  deleteFamily,
  findProfileByEmail,
  getFamily,
  listFamilies,
  removeFamilyMember,
  updateFamily,
} from "@/lib/admin/families";
import { centsToUsd } from "@/lib/billing";
import AutopayStatusChip from "@/components/billing/AutopayStatusChip";
import {
  loadFamilyActivation,
  activationLevel,
  ACTIVATION_TONE,
} from "@/lib/familyActivation";

const DELETE_REASON = {
  has_children:
    "This family still has children linked to it. Withdraw or move them to another family first.",
  has_billing:
    "This family has a billing profile. Remove open invoices first, then retry.",
  blocked:
    "This family can't be deleted — it still has linked records. Remove them first.",
};

export default function AdminFamilies({ activationDeepLink = false }) {
  const { profile } = useAuth();
  const { familyId: routeFamilyId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null); // { mode: 'create'|'edit', familyId? }

  const load = async () => {
    setLoading(true);
    try {
      const list = await listFamilies();
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load families");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-link: /admin/families/:familyId auto-opens the drawer for that row.
  useEffect(() => {
    if (routeFamilyId) {
      setDrawer({ mode: "edit", familyId: routeFamilyId });
    }
  }, [routeFamilyId]);

  const closeDrawer = () => {
    setDrawer(null);
    // If we arrived via /admin/families/:id, strip the param so the back
    // button goes to the list instead of reopening the drawer.
    if (routeFamilyId) navigate("/admin/families", { replace: true });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.display_name || "").toLowerCase().includes(q) ||
        (r.primary_contact_email || "").toLowerCase().includes(q) ||
        (r.primary_contact_name || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    children: rows.reduce((a, r) => a + r.active_child_count, 0),
    open: rows.reduce((a, r) => a + r.open_balance_cents, 0),
  };

  const onDelete = async (familyId, displayName) => {
    if (!window.confirm(`Delete "${displayName}"? This cannot be undone.`)) return;
    try {
      const res = await deleteFamily(familyId);
      if (!res.ok) {
        toast.error(DELETE_REASON[res.reason] || "Could not delete");
        return;
      }
      toast.success(`Deleted ${displayName}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-families" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Families
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Manage enrolled families — contacts, members, children, and
              billing links in one place.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-families-new"
            onClick={() => setDrawer({ mode: "create" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
          >
            <Plus size={14} weight="bold" />
            New family
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi testId="admin-families-kpi-total" label="Families" value={String(kpis.total)} />
        <Kpi testId="admin-families-kpi-children" label="Active children" value={String(kpis.children)} />
        <Kpi testId="admin-families-kpi-open" label="Open balance" value={centsToUsd(kpis.open)} accent />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Search by family name or contact
          </p>
        </div>
        <input
          data-testid="admin-families-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="e.g. Cohen, parent@daycare.test"
          className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="admin-families-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            No families yet
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Families usually appear after enrollment conversion. You can also
            add one manually.
          </p>
        </div>
      ) : (
        <div
          data-testid="admin-families-table"
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                <th className="px-4 py-3">Family</th>
                <th className="px-4 py-3">Primary contact</th>
                <th className="px-4 py-3 text-right">Members</th>
                <th className="px-4 py-3 text-right">Children</th>
                <th className="px-4 py-3 text-right">Open</th>
                <th className="px-4 py-3">Autopay</th>
                <th className="px-4 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`admin-families-row-${r.id}`}
                  className="border-t border-neutral-100"
                >
                  <td className="px-4 py-3 font-semibold text-neutral-900">
                    {r.display_name}
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {r.primary_contact_name ? (
                      <>
                        <span>{r.primary_contact_name}</span>
                        <span className="ml-1 text-xs text-neutral-500">
                          · {r.primary_contact_email || "—"}
                        </span>
                      </>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">{r.member_count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span>{r.active_child_count}</span>
                      <ScheduleMixBadge mix={r.schedule_mix} familyId={r.id} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {centsToUsd(r.open_balance_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <AutopayStatusChip
                      status={r.billing_profile?.autopay_status || "none"}
                      testId={`admin-families-row-${r.id}-autopay`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <button
                        type="button"
                        data-testid={`admin-families-row-${r.id}-open`}
                        onClick={() => setDrawer({ mode: "edit", familyId: r.id })}
                        className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                      >
                        <Pencil size={11} weight="bold" />
                        Manage
                      </button>
                      {r.billing_profile?.id && (
                        <Link
                          to={`/admin/billing/${r.id}`}
                          data-testid={`admin-families-row-${r.id}-billing`}
                          className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
                        >
                          <Receipt size={11} weight="duotone" />
                          Billing
                        </Link>
                      )}
                      <button
                        type="button"
                        data-testid={`admin-families-row-${r.id}-delete`}
                        onClick={() => onDelete(r.id, r.display_name)}
                        aria-label={`Delete ${r.display_name}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                      >
                        <Trash size={11} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <FamilySheet
          mode={drawer.mode}
          familyId={drawer.familyId}
          organizationId={profile?.organization_id}
          focusActivation={activationDeepLink}
          onClose={closeDrawer}
          onSaved={() => { closeDrawer(); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p
        className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}
      >
        {value}
      </p>
    </div>
  );
}

function FamilySheet({ mode, familyId, organizationId, focusActivation = false, onClose, onSaved }) {
  const [family, setFamily] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRelation, setMemberRelation] = useState("parent");
  const [addingMember, setAddingMember] = useState(false);
  // Activation map: profile_id → { push_enabled, has_messaged }. Loaded
  // separately so the sheet renders instantly and the chips fade in
  // when the activation probe completes.
  const [activation, setActivation] = useState(new Map());
  const activationRef = useRef(null);

  // Deep-link: /admin/families/:id/activation. Once members + activation
  // have hydrated, scroll the activation strip into view and add a
  // brief ring highlight so the admin notices what they navigated for.
  useEffect(() => {
    if (!focusActivation || !activationRef.current || activation.size === 0) return;
    activationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    activationRef.current.dataset.deepLinkFlash = "1";
    const t = setTimeout(() => {
      if (activationRef.current) delete activationRef.current.dataset.deepLinkFlash;
    }, 2400);
    return () => clearTimeout(t);
  }, [focusActivation, activation]);

  useEffect(() => {
    if (mode !== "edit" || !familyId) return;
    (async () => {
      try {
        const f = await getFamily(familyId);
        setFamily(f);
        setDisplayName(f?.display_name || "");
      } catch (err) {
        toast.error(err.message || "Could not load family");
      }
    })();
  }, [mode, familyId]);

  // Probe activation for every member profile of the loaded family.
  // Re-runs whenever the member list changes (add / remove member).
  useEffect(() => {
    const ids = (family?.members || [])
      .map((m) => m.profile?.id)
      .filter(Boolean);
    if (ids.length === 0) {
      setActivation(new Map());
      return;
    }
    let alive = true;
    loadFamilyActivation(ids)
      .then((m) => { if (alive) setActivation(m); })
      .catch(() => { if (alive) setActivation(new Map()); });
    return () => { alive = false; };
  }, [family?.members]);

  const onSave = async () => {
    if (!displayName.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        await createFamily({ organizationId, displayName: displayName.trim() });
        toast.success("Family created");
      } else {
        await updateFamily(familyId, { display_name: displayName.trim() });
        toast.success("Family updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onAddMember = async () => {
    const email = memberEmail.trim();
    if (!email) return;
    setAddingMember(true);
    try {
      const foundProfile = await findProfileByEmail(email);
      if (!foundProfile) {
        toast.error("No user with that email. Ask them to sign up first.");
        return;
      }
      await addFamilyMember({
        familyId,
        profileId: foundProfile.id,
        relationship: memberRelation,
        isPrimaryContact: (family?.members || []).length === 0,
      });
      toast.success(`Added ${foundProfile.full_name || email}`);
      const refreshed = await getFamily(familyId);
      setFamily(refreshed);
      setMemberEmail("");
    } catch (err) {
      if (String(err.message || "").includes("duplicate") || err.code === "23505") {
        toast.error("That person is already a member of this family.");
      } else {
        toast.error(err.message || "Could not add member");
      }
    } finally {
      setAddingMember(false);
    }
  };

  const onRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.profile?.full_name || "this member"} from the family?`)) return;
    try {
      await removeFamilyMember(member.id);
      toast.success("Member removed");
      const refreshed = await getFamily(familyId);
      setFamily(refreshed);
    } catch (err) {
      toast.error(err.message || "Remove failed");
    }
  };

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        data-testid="admin-family-sheet"
      >
        <SheetHeader>
          <SheetTitle data-testid="admin-family-sheet-title">
            {mode === "create" ? "New family" : displayName || "Family"}
          </SheetTitle>
          <SheetDescription>
            {mode === "create"
              ? "Start a new family record. You can add parents and children after creating."
              : "Edit the family name, members, and see linked children."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Display name
            </span>
            <input
              data-testid="admin-family-sheet-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Cohen Family"
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="admin-family-sheet-save"
              disabled={saving || !displayName.trim()}
              onClick={onSave}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              {saving ? "Saving…" : mode === "create" ? "Create family" : "Save changes"}
            </button>
          </div>

          {mode === "edit" && family && (
            <>
              <section>
                <div className="mb-2 flex items-center gap-2">
                  <Users size={16} weight="duotone" className="text-primary-hover" />
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                    Members
                  </p>
                </div>
                <ul
                  ref={activationRef}
                  data-testid="admin-family-members"
                  data-focus-activation={focusActivation ? "true" : "false"}
                  className="space-y-1.5 rounded-2xl scroll-mt-6 transition-shadow data-[deep-link-flash=1]:ring-2 data-[deep-link-flash=1]:ring-primary/40 data-[deep-link-flash=1]:ring-offset-2"
                >
                  {(family.members || []).length === 0 ? (
                    <li className="rounded-xl border border-dashed border-neutral-200 p-3 text-xs text-neutral-500">
                      No members yet.
                    </li>
                  ) : (
                    family.members.map((m) => {
                      const a = activation.get(m.profile?.id);
                      const level = activationLevel(a);
                      const tone = ACTIVATION_TONE[level];
                      return (
                      <li
                        key={m.id}
                        data-testid={`admin-family-member-${m.id}`}
                        data-activation={level}
                        className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-neutral-900">
                            {m.profile?.full_name || m.profile?.email || "—"}
                            {m.is_primary_contact && (
                              <span className="ml-2 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover">
                                Primary
                              </span>
                            )}
                          </p>
                          <p className="truncate text-xs text-neutral-500">
                            {m.profile?.email} {m.relationship ? `· ${m.relationship}` : ""}
                          </p>
                          {a && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1">
                              <span
                                data-testid={`admin-family-member-${m.id}-activation`}
                                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider ${tone.pill}`}
                              >
                                {tone.label}
                              </span>
                              <span
                                data-testid={`admin-family-member-${m.id}-push`}
                                title={a.push_enabled ? "Message notifications enabled" : "Message notifications not enabled"}
                                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[9px] font-semibold ${
                                  a.push_enabled
                                    ? "border-primary/30 bg-white text-primary-hover"
                                    : "border-neutral-200 bg-white text-neutral-400"
                                }`}
                              >
                                {a.push_enabled ? <CheckCircle size={9} weight="fill" /> : <BellRinging size={9} weight="duotone" />}
                                Push
                              </span>
                              <span
                                data-testid={`admin-family-member-${m.id}-messaged`}
                                title={a.has_messaged ? "Has messaged a teacher" : "Hasn't messaged a teacher yet"}
                                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[9px] font-semibold ${
                                  a.has_messaged
                                    ? "border-primary/30 bg-white text-primary-hover"
                                    : "border-neutral-200 bg-white text-neutral-400"
                                }`}
                              >
                                {a.has_messaged ? <CheckCircle size={9} weight="fill" /> : <ChatCircleText size={9} weight="duotone" />}
                                Said hi
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          data-testid={`admin-family-member-${m.id}-remove`}
                          onClick={() => onRemoveMember(m)}
                          aria-label="Remove member"
                          className="rounded-full p-1.5 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                        >
                          <X size={14} weight="bold" />
                        </button>
                      </li>
                      );
                    })
                  )}
                </ul>

                <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">
                    <UserPlus size={12} weight="duotone" />
                    Add member
                  </p>
                  <div className="space-y-2">
                    <input
                      data-testid="admin-family-member-email"
                      type="email"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      placeholder="parent@daycare.test"
                      className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <div className="flex gap-2">
                      <select
                        data-testid="admin-family-member-relation"
                        value={memberRelation}
                        onChange={(e) => setMemberRelation(e.target.value)}
                        className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="parent">Parent</option>
                        <option value="guardian">Guardian</option>
                        <option value="grandparent">Grandparent</option>
                        <option value="caregiver">Caregiver</option>
                      </select>
                      <button
                        type="button"
                        data-testid="admin-family-member-add"
                        disabled={addingMember || !memberEmail.trim()}
                        onClick={onAddMember}
                        className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
                      >
                        {addingMember ? "Adding…" : "Add"}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-2 flex items-center gap-2">
                  <Baby size={16} weight="duotone" className="text-primary-hover" />
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                    Children
                  </p>
                </div>
                <ul data-testid="admin-family-children" className="space-y-1.5">
                  {(family.children || []).length === 0 ? (
                    <li className="rounded-xl border border-dashed border-neutral-200 p-3 text-xs text-neutral-500">
                      No children linked. Add them from the Children page.
                    </li>
                  ) : (
                    family.children.map((c) => (
                      <li
                        key={c.id}
                        data-testid={`admin-family-child-${c.id}`}
                        className="flex items-center justify-between rounded-xl border border-neutral-200 p-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-neutral-900">
                            {c.first_name} {c.last_name}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {c.classroom?.name || "No classroom"}
                            {c.withdrawn_at ? " · Withdrawn" : ""}
                          </p>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// ScheduleMixBadge — tiny inline signal next to the children count.
// Calm by default: a family of all full-time children shows nothing extra
// (no clutter for the common case). Mixed or all-part-time households get
// a small pill so admins can spot multi-schedule realities at a glance.
// ---------------------------------------------------------------------------
function ScheduleMixBadge({ mix, familyId }) {
  if (!mix || mix.total === 0 || mix.all_full_time) return null;
  const parts = [];
  if (mix.full_time > 0) parts.push(`${mix.full_time} FT`);
  if (mix.part_time > 0) parts.push(`${mix.part_time} PT`);
  if (mix.custom    > 0) parts.push(`${mix.custom} ✱`);
  const label = parts.join(" · ");
  // Tone: amber when mixed (admin attention helpful), neutral when uniform.
  const tone = mix.is_mixed
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : "border-neutral-200 bg-neutral-50 text-neutral-600";
  return (
    <span
      data-testid={`admin-families-row-${familyId}-schedule-mix`}
      data-mixed={mix.is_mixed ? "true" : "false"}
      title={
        mix.is_mixed
          ? "Children on different schedules"
          : "All children on a non-full-time schedule"
      }
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}
